"use client";

import { toast as reactToastifyToast, ToastOptions } from "react-toastify";
import { CheckCircle, XCircle, AlertCircle, Info, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface CustomToastOptions extends ToastOptions {
  title?: string;
  description?: string;
  type?: "success" | "error" | "warning" | "info";
}

const getIcon = (type: string) => {
  switch (type) {
    case "success":
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    case "error":
      return <XCircle className="h-5 w-5 text-red-500" />;
    case "warning":
      return <AlertCircle className="h-5 w-5 text-yellow-500" />;
    case "info":
      return <Info className="h-5 w-5 text-blue-500" />;
    default:
      return <Info className="h-5 w-5 text-blue-500" />;
  }
};

const getBackgroundColor = (type: string) => {
  switch (type) {
    case "success":
      return "bg-green-50 border-green-200";
    case "error":
      return "bg-red-50 border-red-200";
    case "warning":
      return "bg-yellow-50 border-yellow-200";
    case "info":
      return "bg-blue-50 border-blue-200";
    default:
      return "bg-gray-50 border-gray-200";
  }
};

const CustomToastContent = ({ 
  title, 
  description, 
  type = "info",
  closeToast
}: { 
  title?: string; 
  description?: string; 
  type?: string;
  closeToast?: () => void;
}) => {
  return (
    <div className={cn(
      "max-w-md w-full bg-white border rounded-lg shadow-lg p-4 relative",
      getBackgroundColor(type)
    )}>
      <div className="flex items-start gap-3">
        {getIcon(type)}
        <div className="flex-1 min-w-0">
          {title && (
            <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
          )}
          {description && (
            <div className="text-sm text-gray-600 mt-1 whitespace-pre-line !font-Nunito">
              {description}
            </div>
          )}
        </div>
      </div>
      <button
        onClick={closeToast}
        className="absolute top-[10px] right-[10px] z-10 p-1 hover:bg-gray-100 rounded-full transition-colors"
        aria-label="Close notification"
      >
        <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
      </button>
    </div>
  );
};

// Custom toast functions that match the old API
export const toast = {
  success: (title: string, description?: string, options?: CustomToastOptions) => {
    return reactToastifyToast.success(
      ({ closeToast }: { closeToast: () => void }) => (
        <CustomToastContent 
          title={title} 
          description={description} 
          type="success" 
          closeToast={closeToast}
        />
      ),
      {
        ...options,
        className: "custom-toast-success",
        bodyClassName: "p-0",
        icon: false,
        closeButton: false,
        closeOnClick: false,
        autoClose: 5000,
      }
    );
  },

  error: (title: string, description?: string, options?: CustomToastOptions) => {
    return reactToastifyToast.error(
      ({ closeToast }: { closeToast: () => void }) => (
        <CustomToastContent 
          title={title} 
          description={description} 
          type="error" 
          closeToast={closeToast}
        />
      ),
      {
        ...options,
        className: "custom-toast-error",
        bodyClassName: "p-0",
        icon: false,
        closeButton: false,
        closeOnClick: false,
        autoClose: 5000,
      }
    );
  },

  warning: (title: string, description?: string, options?: CustomToastOptions) => {
    return reactToastifyToast.warning(
      ({ closeToast }: { closeToast: () => void }) => (
        <CustomToastContent 
          title={title} 
          description={description} 
          type="warning" 
          closeToast={closeToast}
        />
      ),
      {
        ...options,
        className: "custom-toast-warning",
        bodyClassName: "p-0",
        icon: false,
        closeButton: false,
        closeOnClick: false,
        autoClose: 5000,
      }
    );
  },

  info: (title: string, description?: string, options?: CustomToastOptions) => {
    return reactToastifyToast.info(
      ({ closeToast }: { closeToast: () => void }) => (
        <CustomToastContent 
          title={title} 
          description={description} 
          type="info" 
          closeToast={closeToast}
        />
      ),
      {
        ...options,
        className: "custom-toast-info",
        bodyClassName: "p-0",
        icon: false,
        closeButton: false,
        closeOnClick: false,
        autoClose: 5000,
      }
    );
  },

  // Support for simple messages (backward compatibility)
  default: (message: string, options?: CustomToastOptions) => {
    return reactToastifyToast.info(
      ({ closeToast }: { closeToast: () => void }) => (
        <CustomToastContent 
          title={message} 
          type="info" 
          closeToast={closeToast}
        />
      ),
      {
        ...options,
        className: "custom-toast-info",
        bodyClassName: "p-0",
        icon: false,
        closeButton: false,
        closeOnClick: false,
        autoClose: 5000,
      }
    );
  },
};

// Export ToastOptions for TypeScript support
export type { ToastOptions };
